import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, KeyboardArrowDown, KeyboardArrowRight, Close } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmt     = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '-';
  // API returns dd/mm/yyyy format directly — return as-is
  if (typeof d === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
  // Handle .NET JSON date format: /Date(1234567890)/
  if (typeof d === 'string' && d.startsWith('/Date(')) {
    const ms = parseInt(d.replace(/\/Date\((\d+)[^)]*\)\//, '$1'));
    if (!isNaN(ms)) {
      const dt = new Date(ms);
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
    }
  }
  // ISO or other formats
  const parsed = new Date(d);
  if (isNaN(parsed)) return '-';
  return `${String(parsed.getDate()).padStart(2,'0')}/${String(parsed.getMonth()+1).padStart(2,'0')}/${parsed.getFullYear()}`;
};
const fmtQty  = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmt2Date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const PRIMARY = '#1c2266';
const PRIMARY_MID = '#3f51b5';
const PRIMARY_LT = '#e8eaf6';
const th  = { padding: '8px 10px', textAlign: 'left',   fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
const td  = { padding: '7px 10px', textAlign: 'left',   fontSize: 12, whiteSpace: 'nowrap' };
const tdR = { padding: '7px 10px', textAlign: 'right',  fontSize: 12, whiteSpace: 'nowrap' };
const tdC = { padding: '7px 10px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' };

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const SaleInvoiceRegister = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);
  const [search, setSearch]     = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData(
        'SaleReportApi',
        `/SaleInvoiceRegistrationDetails/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
      setExpanded({});
    } catch (err) {
      console.error(err);
      setError('Failed to fetch sale invoice register');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  // Group: RN=1 rows are master (have BillNo + bill totals), all rows have item data
  // Each unique SaleId = one bill, all rows for that SaleId = items
  const masters = useMemo(() => {
    const map = new Map();
    data.forEach(row => {
      if (!map.has(row.SaleId)) {
        map.set(row.SaleId, {
          SaleId: row.SaleId,
          BillNo: row.BillNo,
          BillDate: row.BillDate,
          CustomerName: row.CustomerName,
          GSTNumber: row.GSTNumber,
          BillTaxable: row.BillTaxable,
          BillCGST: row.BillCGST,
          BillSGST: row.BillSGST,
          BillIGST: row.BillIGST,
          GrandTotal: row.GrandTotal,
          CashAmount: row.CashAmount,
          CardAmount: row.CardAmount,
          UPIAmount: row.UPIAmount,
          CreditAmount: row.CreditAmount,
          PaymentType: row.PaymentType,
          items: [],
        });
      }
      // Every row has item data
      if (row.ItemName) {
        map.get(row.SaleId).items.push({
          ItemName: row.ItemName,
          BatchNumber: row.BatchNumber,
          Quantity: row.Quantity,
          Rate: row.Rate,
          DiscountAmount: row.DiscountAmount,
          TaxableAmount: row.TaxableAmount,
          GSTRate: row.GSTRate,
          CGSTAmount: row.CGSTAmount,
          SGSTAmount: row.SGSTAmount,
          IGSTAmount: row.IGSTAmount,
          TotalAmount: row.TotalAmount,
        });
      }
    });
    return Array.from(map.values());
  }, [data]);

  const filtered = useMemo(() => {
    if (!search.trim()) return masters;
    const q = search.toLowerCase();
    return masters.filter(m =>
      (m.BillNo       || '').toLowerCase().includes(q) ||
      (m.CustomerName || '').toLowerCase().includes(q) ||
      (m.GSTNumber    || '').toLowerCase().includes(q)
    );
  }, [masters, search]);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  // Totals
  const totalTaxable = filtered.reduce((s, r) => s + (parseFloat(r.BillTaxable) || 0), 0);
  const totalCGST    = filtered.reduce((s, r) => s + (parseFloat(r.BillCGST)    || 0), 0);
  const totalSGST    = filtered.reduce((s, r) => s + (parseFloat(r.BillSGST)    || 0), 0);
  const totalIGST    = filtered.reduce((s, r) => s + (parseFloat(r.BillIGST)    || 0), 0);
  const totalAmount  = filtered.reduce((s, r) => s + (parseFloat(r.GrandTotal)  || 0), 0);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'SaleInvoiceRegister', 0);
    if (!filtered.length) return;
    const colHeaders = ['#', 'Bill No', 'Date', 'Customer', 'GST No', 'Taxable', 'CGST', 'SGST', 'IGST', 'Grand Total', 'Payment', '', 'Item', 'Batch', 'Qty', 'Rate', 'Discount', 'Taxable', 'GST%', 'CGST', 'SGST', 'IGST', 'Total'];
    const rows = [];
    filtered.forEach((m, i) => {
      rows.push([i + 1, m.BillNo, fmtDate(m.BillDate), m.CustomerName, m.GSTNumber || '',
        parseFloat(m.BillTaxable) || 0, parseFloat(m.BillCGST) || 0, parseFloat(m.BillSGST) || 0,
        parseFloat(m.BillIGST) || 0, parseFloat(m.GrandTotal) || 0, m.PaymentType || '',
        '', '', '', '', '', '', '', '', '', '', '', '']);
      m.items.forEach(it => {
        rows.push(['', '', '', '', '', '', '', '', '', '', '', '',
          it.ItemName, it.BatchNumber || '-', parseFloat(it.Quantity) || 0, parseFloat(it.Rate) || 0,
          parseFloat(it.DiscountAmount) || 0, parseFloat(it.TaxableAmount) || 0,
          parseFloat(it.GSTRate) || 0, parseFloat(it.CGSTAmount) || 0,
          parseFloat(it.SGSTAmount) || 0, parseFloat(it.IGSTAmount) || 0,
          parseFloat(it.TotalAmount) || 0]);
      });
    });

      await exportStyledExcel({
          reportTitle: 'SALE INVOICE REGISTER',
          sheetName: 'Sale Invoice Register',
          fileName: `SaleInvoiceRegister_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: colHeaders,
          rows,
      });
  };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading sale invoice register..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #2d3494 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Sale Invoice Register</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          {searchOpen ? (
            <>
              <TextField autoFocus size="small" placeholder="Search bill, customer..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
                  endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}><Close sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                }}
                sx={{ flexShrink: 0, width: 180, '& .MuiOutlinedInput-root': { height: 29, fontSize: 12, borderRadius: '8px', color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' } }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } }}
              />
              <Tooltip title="Close" arrow>
                <IconButton size="small" onClick={() => { setSearchOpen(false); setSearch(''); }} sx={iconBtn}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Search" arrow>
              <IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}>
                <Search sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate}   onChange={e => setToDate(e.target.value)}   style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1000 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={{ ...th, width: 32 }}></th>
                <th style={th}>#</th>
                <th style={th}>Bill No</th>
                <th style={th}>Date</th>
                <th style={th}>Customer</th>
                <th style={th}>GST No</th>
                <th style={{ ...th, textAlign: 'right' }}>Taxable</th>
                <th style={{ ...th, textAlign: 'right' }}>CGST</th>
                <th style={{ ...th, textAlign: 'right' }}>SGST</th>
                <th style={{ ...th, textAlign: 'right' }}>IGST</th>
                <th style={{ ...th, textAlign: 'right' }}>Grand Total</th>
                <th style={{ ...th, textAlign: 'center' }}>Payment</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={12} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select date range and click refresh'}
                  </td>
                </tr>
              ) : (
                filtered.map((m, i) => {
                  const isOpen = !!expanded[m.BillNo];
                  return (
                    <>
                      {/* Master Row */}
                      <tr key={m.BillNo} onClick={() => toggleExpand(m.BillNo)}
                        style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
                        <td style={{ ...tdC, width: 32 }}>
                          {isOpen ? <KeyboardArrowDown sx={{ fontSize: 16, color: PRIMARY }} /> : <KeyboardArrowRight sx={{ fontSize: 16, color: '#9ca3af' }} />}
                        </td>
                        <td style={td}>{i + 1}</td>
                        <td style={{ ...td, fontWeight: 700, color: PRIMARY }}>{m.BillNo}</td>
                        <td style={td}>{fmtDate(m.BillDate)}</td>
                        <td style={{ ...td, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.CustomerName}</td>
                        <td style={{ ...td, color: '#64748b', fontSize: 11 }}>{m.GSTNumber || '-'}</td>
                        <td style={tdR}>{fmt(m.BillTaxable)}</td>
                        <td style={tdR}>{fmt(m.BillCGST)}</td>
                        <td style={tdR}>{fmt(m.BillSGST)}</td>
                        <td style={tdR}>{fmt(m.BillIGST)}</td>
                        <td style={{ ...tdR, fontWeight: 700 }}>{fmt(m.GrandTotal)}</td>
                        <td style={tdC}>
                          <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: PRIMARY_LT, color: PRIMARY }}>
                            {m.PaymentType || '-'}
                          </span>
                        </td>
                      </tr>

                      {/* Item Sub-Header */}
                      {isOpen && (
                        <tr style={{ background: '#c7d2fe', borderBottom: '1px solid #a5b4fc' }}>
                          <td style={tdC}></td>
                          <td style={{ ...td, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>#</td>
                          <td style={{ ...td, fontWeight: 700, fontSize: 11, color: '#1e1b4b', paddingLeft: 24 }}>Item Name</td>
                          <td style={{ ...td, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>Batch</td>
                          <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>Qty</td>
                          <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>Rate</td>
                          <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>Discount</td>
                          <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>Taxable</td>
                          <td style={{ ...tdC, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>GST%</td>
                          <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>CGST</td>
                          <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>SGST</td>
                          <td style={{ ...tdR, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>Total</td>
                        </tr>
                      )}

                      {/* Item Rows */}
                      {isOpen && m.items.map((it, j) => (
                        <tr key={`${m.BillNo}-${j}`} style={{ background: '#eef2ff', borderBottom: '1px solid #e0e7ff' }}>
                          <td style={tdC}></td>
                          <td style={{ ...td, color: '#6b7280' }}>{j + 1}</td>
                          <td style={{ ...td, fontWeight: 600, color: '#1e293b', paddingLeft: 24 }}>{it.ItemName}</td>
                          <td style={{ ...td, color: '#64748b', fontSize: 11 }}>{it.BatchNumber || '-'}</td>
                          <td style={tdR}>{fmtQty(it.Quantity)}</td>
                          <td style={tdR}>{fmt(it.Rate)}</td>
                          <td style={{ ...tdR, color: '#dc2626' }}>{fmt(it.DiscountAmount)}</td>
                          <td style={tdR}>{fmt(it.TaxableAmount)}</td>
                          <td style={tdC}>{it.GSTRate}%</td>
                          <td style={tdR}>{fmt(it.CGSTAmount)}</td>
                          <td style={tdR}>{fmt(it.SGSTAmount)}</td>
                          <td style={{ ...tdR, fontWeight: 600 }}>{fmt(it.TotalAmount)}</td>
                        </tr>
                      ))}
                    </>
                  );
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700 }}>
                  <td colSpan={6} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                  <td style={tdR}>{fmt(totalTaxable)}</td>
                  <td style={tdR}>{fmt(totalCGST)}</td>
                  <td style={tdR}>{fmt(totalSGST)}</td>
                  <td style={tdR}>{fmt(totalIGST)}</td>
                  <td style={tdR}>{fmt(totalAmount)}</td>
                  <td style={td}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default SaleInvoiceRegister;




